/* eslint-disable @typescript-eslint/no-explicit-any */
import { History, Location } from 'history';
import { RouteProps, matchPath } from 'react-router';
import { defaultMemoize } from 'reselect';
import { defaults } from 'es-toolkit/compat';
import { noop } from 'es-toolkit';

export interface ScrollDepthOptions {
  speed?: number;
  easing?: string;
  minimumScrollTime?: number;
  maximumScrollTime?: number;
  offset?: number;
  horizontal?: boolean;
}

export function scrollToDepth(container: any, targetPosition: any, options: any) {
  const defaultOptions = {
    speed: 2500,
    easing: 'easeInOutSine',
    minimumScrollTime: 0.1,
    maximumScrollTime: 0.25,
    offset: 0,
    horizontal: false,
  };

  const actualOptions = defaults(options, defaultOptions);
  let scrollPosition = 0;

  if (actualOptions.horizontal) {
    scrollPosition = container.scrollLeft;
  } else {
    scrollPosition = container.scrollTop;
  }

  const targetWithMargin = targetPosition + actualOptions.offset;
  const minimumScrollTime = actualOptions.minimumScrollTime;
  const maximumScrollTime = actualOptions.maximumScrollTime;

  const delta = Math.abs(
    (actualOptions.horizontal ? container.scrollLeft : container.scrollTop) - targetWithMargin
  );
  const animationTime = Math.max(
    minimumScrollTime,
    Math.min(delta / actualOptions.speed, maximumScrollTime)
  );

  const easingEquations = {
    easeOutSine: (pos: any): number => {
      return Math.sin(pos * (Math.PI / 2));
    },
    easeInOutSine: (pos: any): number => {
      return (Math.cos(Math.PI * pos) - 1) * -0.5;
    },
  };

  let currentTime = 0;
  let updatedScrollPosition = 0;
  const frameTime = 1 / 60.0;

  // Animation loop
  function tick() {
    currentTime += frameTime;
    const p = currentTime / animationTime;
    const t: number = (easingEquations as any)[actualOptions.easing](p);

    if (p < 1) {
      updatedScrollPosition = scrollPosition + (targetWithMargin - scrollPosition) * t;
    } else {
      updatedScrollPosition = targetWithMargin;
    }

    if (actualOptions.horizontal) {
      container.scrollLeft = updatedScrollPosition;
    } else {
      container.scrollTop = updatedScrollPosition;
    }

    if (p < 1) {
      (window as any).requestAnimationFrame(tick);
    }
  }

  tick();
}

// Modified version of the code found here:
// http://stackoverflow.com/questions/12199363/scrollto-with-animation
// Speed controls relative speed of the animation, between a hard coded minimum
// and maximum time
export function scrollToChild(
  container: HTMLElement | null,
  child: HTMLElement | null,
  options: ScrollDepthOptions
) {
  const targetY = (child?.offsetTop ?? 0) - (container?.offsetTop ?? 0);
  const targetX = (child?.offsetLeft ?? 0) - (container?.offsetLeft ?? 0);
  scrollToDepth(container, options.horizontal ? targetX : targetY, options);
}

export function scrollBodyToChild(child: any, options: any) {
  const childRect = child.getBoundingClientRect();

  const isInViewport = childRect.top > 0 && childRect.bottom < window.innerHeight;

  if (!isInViewport) {
    const targetY =
      childRect.top +
      (window as any).pageYOffset -
      window.innerHeight +
      Math.min(childRect.height, window.innerHeight);

    scrollToDepth(document.body, targetY, options);
    scrollToDepth(document.documentElement, targetY, options);
  }
}

export function getScrollTop() {
  if (document.body && document.body.scrollTop) {
    return document.body.scrollTop;
  } else if (document.documentElement && document.documentElement.scrollTop) {
    return document.documentElement.scrollTop;
  }
  return 0;
}

export const isBlackListedLocation = (location: Location, blackList: RouteProps[]) => {
  const url = stringifyLocation(location);
  return blackList.some((value) => !!matchPath(url, value));
};

const stringifyLocation = defaultMemoize((location: Location) => {
  return location.pathname;
});

export const supportsPassive = () => {
  let supportsPassive = false;
  if (BROWSER) {
    try {
      const opts = Object.defineProperty({}, 'passive', {
        //eslint-disable-next-line getter-return
        get() {
          supportsPassive = true;
        },
      });
      (window as any).addEventListener('testPassive', null, opts);
      (window as any).removeEventListener('testPassive', null, opts);
    } catch (_error) {
      noop();
    }
  }
  return supportsPassive;
};

export function scrollMemory(browserHistory: History, blackList: RouteProps[] = []): () => void {
  const memory: any = {};
  const seenKeys: any = {};
  let lastLocationPath = '';

  const save = () => {
    const { location } = browserHistory;
    const url = stringifyLocation(location);
    memory[url] = getScrollTop();
  };

  // fired when the browser changes urls
  const restore = (location: Location) => {
    const url = stringifyLocation(location);

    const haveSeenUrlAndKey =
      memory[url] !== undefined && memory[url] !== 0 && location.key && seenKeys[location.key];

    if (haveSeenUrlAndKey) {
      // there's been a url change and we have a new scroll
      // position to restore
      setTimeout(() => {
        window.scrollTo(0, memory[url]);
      }, 0);
    }
  };

  // fired when the browser changes urls
  // On click link to current page, scroll to top if not seen
  const scrollToTopIfNecessary = (location: Location) => {
    const url = stringifyLocation(location);

    const isUnseenCurrentPagePath =
      lastLocationPath === location.pathname && location.key && !seenKeys[location.key];

    const isNewPage = !memory[url] && location.key && !seenKeys[location.key];
    // note: this doesn't affect modal routes because the background scroll has been locked
    if (isUnseenCurrentPagePath || isNewPage) {
      setTimeout(() => {
        scrollToDepth(document.body, 0, {});
        scrollToDepth(document.documentElement, 0, {});
      }, 0);
    }
  };

  window.addEventListener('scroll', save, supportsPassive() ? { passive: true } : false);
  window.addEventListener('click', save);

  const cancelListener = browserHistory.listen((location: Location) => {
    if (isBlackListedLocation(location, blackList)) {
      return;
    }
    scrollToTopIfNecessary(location);
    restore(location);
    if (location.key) {
      seenKeys[location.key] = true;
    }
    lastLocationPath = location.pathname;
  });

  return cancelListener;
}
